import { Card, EmptyState, Flex, SimpleTable, Typography } from "djuno-design";
import ExpandableCard from "../components/ExpandableCard";

const { Title, Text } = Typography;

const EmptyStatePage = () => {
  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>EmptyState</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            The EmptyState component in Djuno Design provides a customizable UI
            for displaying empty states or no-data situations. It allows for
            various customizations, including icons, text, and additional
            classes, making it versatile for different use cases.
          </Text>

          <Title level={5}>Variants and Customization</Title>
          <Text size="sm">
            The EmptyState component offers the following customization options:
          </Text>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Content
                </Text>
                : Customize the content displayed in the empty state, including
                optional icons and text.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Icon
                </Text>
                : Choose to display an icon or not, and provide a custom icon if
                desired.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Class Names
                </Text>
                : Apply additional custom styles through class names for the
                icon and text.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Text
                </Text>
                : Specify the text to display when there is no data available.
              </Text>
            </li>
          </ul>
        </Flex>

        {/* Examples */}
        <Flex direction="col" className="gap-4 mt-5 w-full">
          <Flex
            direction={{ default: "col", sm: "row" }}
            className="gap-4 w-full"
          >
            <ExpandableCard
              description="Empty State with Simple Icon"
              codeViewerProps={{
                code: EmptyStateExample1,
              }}
            >
              <EmptyState
                text="Empty state"
                icon={<EmptyState.PRESENTED_IMAGE_SIMPLE />}
              />
            </ExpandableCard>
            <ExpandableCard
              description="Empty State with Default Icon"
              codeViewerProps={{
                code: EmptyStateExample2,
              }}
            >
              <EmptyState
                text="Empty state"
                icon={<EmptyState.PRESENTED_IMAGE_DEFAULT />}
              />
            </ExpandableCard>
            <ExpandableCard
              description="Empty State with Undefined Icon"
              codeViewerProps={{
                code: EmptyStateExample3,
              }}
            >
              <EmptyState text="Empty state" />
            </ExpandableCard>
          </Flex>

          <Flex
            direction={{ default: "col", md: "row" }}
            className="gap-4 w-full"
          >
            <ExpandableCard
              description="Empty State Without Icon"
              codeViewerProps={{
                code: EmptyStateExample4,
              }}
            >
              <EmptyState text="Empty state" usingIcon={false} />
            </ExpandableCard>
            <ExpandableCard
              description="Empty State with Default Text"
              codeViewerProps={{
                code: EmptyStateExample5,
              }}
            >
              <EmptyState usingIcon={false} />
            </ExpandableCard>
            <ExpandableCard
              description="Empty State Without Text"
              codeViewerProps={{
                code: EmptyStateExample6,
              }}
            >
              <EmptyState usingText={false} />
            </ExpandableCard>
          </Flex>
        </Flex>

        {/* API */}
        <Flex direction="col" className="w-full mt-5">
          <Title level={5}>API</Title>
          <Flex direction="col" className="gap-1 w-full">
            <Card id="API">
              <SimpleTable className="w-full">
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* children */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">children</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The content to be displayed inside the empty state. Can
                        include text and/or icons.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`React.ReactNode | string`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">No data</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>

                  {/* icon */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">icon</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The icon to be displayed in the empty state. Can be a
                        React node.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`React.ReactNode`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code className="whitespace-nowrap">
                        {`<EmptyState.PRESENTED_IMAGE_DEFAULT />`}
                      </Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* iconClassName */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">iconClassName</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">CSS class for styling the icon.</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* textClassName */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">textClassName</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">CSS class for styling the text.</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* usingIcon */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">usingIcon</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Indicates whether to display the icon.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">true</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* usingText */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">usingText</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Indicates whether to display the text.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">true</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

const EmptyStateExample1 = `<EmptyState
  text="Empty state"
  icon={<EmptyState.PRESENTED_IMAGE_SIMPLE />}
/>`;
const EmptyStateExample2 = `<EmptyState
  text="Empty state"
  icon={<EmptyState.PRESENTED_IMAGE_DEFAULT />}
/>`;
const EmptyStateExample3 = `<EmptyState text="Empty state" />`;
const EmptyStateExample4 = `<EmptyState text="Empty state" usingIcon={false} />`;
const EmptyStateExample5 = ` <EmptyState usingIcon={false} />`;
const EmptyStateExample6 = ` <EmptyState usingText={false} />`;

export default EmptyStatePage;
